import s from './LoginForm.module.scss'
import React, { useCallback, useContext, useState } from 'react'
import { Button, Divider, Form } from 'antd'
import { WalletOutlined } from '@ant-design/icons'
import {
    InputControl,
    InputPasswordControl,
} from 'shared/components/InputControl'
import { AuthService } from 'api/http'
import Logo from 'shared/img/logo.svg'
import { AccountContext, LoaderContext } from 'core/context'
import { PopupAdapterFormProps } from 'shared/components/popups/PopupAdapter.types'
import { LoginModel } from 'api/http/models'
import ReCAPTCHA from 'react-google-recaptcha'
import cn from 'classnames'
import translations from 'shared/components/LoginForm/translations'
import { useLanguage } from 'context/LanguageContext'

export const LoginForm: React.FC<PopupAdapterFormProps> = React.memo(
    ({ onRequestFinish, id }) => {
        const { auth, tronLinkAuth } = useContext(AccountContext)
        const { setLoader, isLoader } = useContext(LoaderContext)
        const [captchaError, setCaptchaError] = useState(false)
        const [captcha, setCaptcha] = useState<string | null>()

        const { language } = useLanguage()
        const t = translations[language]

        const handleFinish = useCallback(
            async (values: LoginModel) => {
                try {
                    if (!captcha) {
                        setCaptchaError(true)
                        return
                    }
                    setLoader(true)

                    await AuthService.login(values)
                    await auth()

                    onRequestFinish?.()
                } catch (e) {
                    console.log(e)
                } finally {
                    setLoader(false)
                }
            },
            [auth, onRequestFinish, setLoader, captcha]
        )

        return (
            <Form<LoginModel>
                id={id}
                onFinish={handleFinish}
                validateMessages={{ required: t.REQUIRED_FIELD }}
                className={s.wrapper}
                size="large"
            >
                <img src={Logo} alt="RealColibri" className={s.logo} />

                <h2 className={s.title}>{t.WELCOME_BACK}</h2>
                <p className={s.subtitle}>{t.SUBTITLE}</p>

                {/* TronLink — основной способ входа.  Кнопку показываем всегда:
                    клик сам инициирует подключение/разблокировку кошелька, даже
                    если авто-коннект на загрузке не прошёл. */}
                {!isLoader && (
                    <>
                        <Button
                            type="primary"
                            size="large"
                            block
                            icon={<WalletOutlined />}
                            className={s.tronBtn}
                            onClick={() => tronLinkAuth()}
                        >
                            {t.LOGIN_WITH_TRONLINK}
                        </Button>
                        <p className={s.hint}>{t.TRONLINK_PROMPT}</p>
                    </>
                )}

                <Divider className={s.divider}>{t.OR}</Divider>

                {/* Логин/пароль — для существующих аккаунтов */}
                <Form.Item name="username" rules={[{ required: true }]}>
                    <InputControl placeholder={t.USERNAME_PLACEHOLDER} />
                </Form.Item>

                <Form.Item name="password" rules={[{ required: true }]}>
                    <InputPasswordControl placeholder={t.PASSWORD_PLACEHOLDER} />
                </Form.Item>

                {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
                    <div className={cn(s.captchaBox, captchaError && s.captcha)}>
                        <ReCAPTCHA
                            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                            onChange={setCaptcha}
                        />
                    </div>
                )}

                <Button htmlType="submit" block className={s.submit}>
                    {t.LOGIN}
                </Button>
            </Form>
        )
    }
)
